import { Center, HStack, Link, ListItem, Spinner, Text, UnorderedList } from '@chakra-ui/react';
import React from 'react';

import TextWithTooltip from '../../../../../components/TextWithTooltip';
import { TITLES_PHONE } from '../../../../../constants/contact';
import { GOOGLE } from '../../../../../constants/urls';
import { DealType } from '../../../../../gql/generated/graphql';
import { useJurisdictionDocs } from '../../../../../hooks';
import { DealWithFarthestState } from '../../../utils';
import { formatBank, formatFirstPaymentDate, formatLienholder } from '../utils';

type Props = { deal?: DealWithFarthestState };

export const AwaitingBankFundingBody = ({ deal }: Props) => (
  <Text>
    We're getting your loan funded by {formatBank(deal)}. You're almost there! We'll email you as
    soon as it's finalized and keep you updated along the way.
  </Text>
);

export const LoanPayoffBody = ({ deal }: Props) => {
  const isRefi = deal?.type === DealType.Refi;
  const lienholder = formatLienholder(deal);
  const bank = formatBank(deal);

  return (
    <>
      <Text mb={4}>
        We've started paying off your {isRefi ? 'loan' : 'lease'} with {lienholder}. The car is
        almost officially yours! If you have a {isRefi ? 'loan' : 'lease'} payment due within the
        next 7 days, please make that payment to avoid any discrepancies.
      </Text>
      <Text>
        You'll receive a welcome packet from {bank} soon. When you do, update your auto insurance to
        replace {lienholder} with {bank}.
      </Text>
    </>
  );
};

export const AwaitingTitleBody = ({ deal }: Props) => {
  const isRefi = deal?.type === DealType.Refi;
  const lienholder = formatLienholder(deal);
  const { requiredDocs, loading } = useJurisdictionDocs(deal, { skip: isRefi });

  return (
    <>
      <Text mb={4}>
        Great news! Your {isRefi ? 'loan' : 'lease'} account is officially closed with {lienholder}.
        This may not reflect on your online account right away, as it can take 10-14 business days
        for the funds to process.
      </Text>
      <Text mb={4}>
        Once those funds process, {lienholder} will send us your title so we can process it. If
        you've recently moved, or if any of your driver's license or car insurance are set to expire
        in the next 90 days, renew them and provide us a copy as soon as possible.
      </Text>
      {!isRefi && loading && (
        <Center mb={4}>
          <HStack spacing={2}>
            <Spinner size="sm" speed="0.8s" />
            <Text fontSize="sm">Checking required documents...</Text>
          </HStack>
        </Center>
      )}
      {!isRefi && !loading && requiredDocs.length > 0 && (
        <>
          <Text mb={2}>To avoid delays, please ensure you have the following documents ready:</Text>
          <UnorderedList mb={4}>
            {requiredDocs.map((doc) => (
              <ListItem key={doc.title}>
                <TextWithTooltip
                  label={
                    <>
                      {doc.whyWeAsk} {doc.howToGetIt}
                    </>
                  }
                >
                  {doc.title}
                </TextWithTooltip>
              </ListItem>
            ))}
          </UnorderedList>
        </>
      )}
      <Text>If we need anything from you, we'll reach out.</Text>
    </>
  );
};

export const TitleReceivedBody = () => (
  <>
    <Text mb={4}>
      Your vehicle title has arrived. Exciting! We're preparing your paperwork for final processing
      with the state and coordinating with the DMV on your behalf.
    </Text>
    <Text>
      Please double-check your documents. Is anything expiring soon? Have you recently moved or
      changed your name? If so, call us at{' '}
      <Link href={`tel:+1-${TITLES_PHONE}`}>{TITLES_PHONE}</Link> to avoid delays.
    </Text>
  </>
);

export const TitleProcessingBody = () => (
  <>
    <Text mb={4}>
      We've sent your title paperwork to the DMV for final state approval. We're in the home
      stretch! Processing times vary and are outside our control, but we've done everything we can
      on our end.
    </Text>
    <Text>
      To help prevent delays, let our title team know if any documents are expiring soon. We'll keep
      you posted.
    </Text>
  </>
);

export const CongratsBody = ({ deal }: Props) => (
  <>
    <Text mb={4}>
      Your vehicle's title has been updated to reflect your new bank, and the car is officially
      yours! Your first payment is due on <b>{formatFirstPaymentDate(deal)}</b>.
    </Text>
    <Text mb={4}>
      We'd love to hear about your experience. Would you mind writing a{' '}
      <Link isExternal href={GOOGLE}>
        Google review
      </Link>
      ? It helps other drivers like you.
    </Text>
    <Text>Thank you for choosing Lease End. Enjoy your car!</Text>
  </>
);
